import { TextField, Theme } from '@mui/material';
import {
  ChangeEvent,
  KeyboardEvent,
  ReactNode,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import { FormProps } from '..';
import { createUseStyles } from 'react-jss';
import Icon from '../../icon';
import CopyClipButton from '@/components/copy-clip-button';
import { IconType } from '@/components/icon/helpers';

interface Props extends FormProps {
  value: string;
  onChange?: (value: string, event?: ChangeEvent<HTMLInputElement>) => void;
  onEnter?: (value: string) => void;
  type?: string;
  autoComplete?: string;
  rows?: number;
  placeholder?: string;
  clearable?: boolean;
  startIcon?: IconType;
  copyClip?: boolean;
  readonly?: boolean;
  maxLength?: number;
  noBorder?: boolean;
}

const useStyles = createUseStyles<string, { isTextarea: boolean; noBorder: boolean }>(
  (theme: Theme) => ({
    textfield: ({ isTextarea }) => ({
      '& label.MuiFormLabel-root.Mui-focused': {
        color: theme.palette.common.black,
      },
      '& .MuiInputBase-root': {
        paddingRight: isTextarea ? 0 : 6,
        '&.Mui-focused': {
          '& fieldset': {
            borderColor: theme.palette.grey[500],
          },
        },
        '&.Mui-readOnly fieldset': {
          borderColor: `${theme.palette.grey[200]} !important`,
        },
        '&.MuiInputBase-adornedStart': {
          paddingLeft: 6,
        },
        '& input.MuiInputBase-inputAdornedEnd': {
          paddingRight: 12,
        },
        '& input.Mui-readOnly, textarea.Mui-readOnly': {
          color: `${theme.palette.grey[600]} !important`,
        },
      },
      '& .MuiFormHelperText-root': {
        marginLeft: 0,
      },
      ...(isTextarea && {
        '& textarea': {
          paddingRight: 14,
        },
      }),
    }),
    startAdornment: {
      marginTop: 2,
    },
    tiny: {
      '& input.MuiInputBase-input': {
        fontSize: '0.9rem',
        padding: '6px 4px',
      },
      '& $startAdornment': {
        marginTop: 2,
      },
    },
  })
);

export interface TextInputInterface {
  focus: () => void;
}

const TextInput = forwardRef<TextInputInterface, Props>(
  (
    {
      name,
      label = '',
      onChange,
      onEnter,
      type = 'text',
      autoComplete = '',
      autoFocus = false,
      value,
      error = '',
      disabled = false,
      required = false,
      rows,
      size = 'medium',
      placeholder,
      clearable = false,
      copyClip = false,
      startIcon,
      readonly = false,
      maxLength,
      fullWidth = true,
      noBorder = false,
    },
    ref
  ) => {
    const isSmall = ['tiny', 'small'].includes(size);
    const isTextarea = type === 'textarea';
    const styles = useStyles({ isTextarea, noBorder });

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      if (readonly) {
        return;
      }
      onChange && onChange(String(event.target.value), event);
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' && onEnter) {
        event.preventDefault();
        onEnter(value);
      }
    };

    const TextareaProps = {
      multiline: false,
      minRows: 5,
      maxRows: 5,
    };

    if (isTextarea) {
      TextareaProps.multiline = true;
      if (rows) {
        TextareaProps.minRows = rows;
        TextareaProps.maxRows = rows;
      }
    }

    const handleClear = () => {
      if (readonly) {
        return;
      }
      onChange && onChange('');
    };

    let EndAdornmentEl: ReactNode = null;
    const showClearIcon = clearable && !!value;
    const hasStartIcon = !!startIcon;

    if (showClearIcon) {
      EndAdornmentEl = <Icon className={styles.endAdornment} name="close" onClick={handleClear} />;
    }

    if (copyClip) {
      EndAdornmentEl = <CopyClipButton className={styles.endAdornment} value={value || ''} />;
    }

    const inputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => {
      return {
        focus: () => {
          inputRef.current?.focus();
        },
      };
    });

    // make sure autofocus actually works
    // https://stackoverflow.com/questions/60476155/is-it-safe-to-use-ref-current-as-useeffects-dependency-when-ref-points-to-a-dom
    useEffect(() => {
      if (!autoFocus) {
        return;
      }
      setTimeout(() => {
        inputRef.current?.focus();
      });
    }, [autoFocus]);

    return (
      <TextField
        id={name}
        name={name}
        label={label || undefined}
        variant="outlined"
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        value={value || ''}
        margin={isSmall ? 'none' : 'normal'}
        fullWidth={fullWidth}
        type={type}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        error={!!error}
        helperText={error}
        disabled={disabled}
        {...TextareaProps}
        className={styles.textfield}
        required={required}
        size={isSmall ? 'small' : 'medium'}
        placeholder={placeholder}
        inputRef={inputRef}
        inputProps={{ maxLength }}
        InputProps={{
          classes: {
            root: size === 'tiny' ? styles.tiny : undefined,
          },
          startAdornment: hasStartIcon ? (
            <Icon className={styles.startAdornment} name={startIcon} />
          ) : null,
          endAdornment: EndAdornmentEl,
          readOnly: readonly,
        }}
        sx={{
          '& fieldset.MuiOutlinedInput-notchedOutline': {
            borderWidth: noBorder ? '0 !important' : '1px !important',
          },
        }}
      />
    );
  }
);

export default TextInput;
